import React from 'react'
import { RedImmobile } from '../../../types/red'

interface CheckItem {
  label: string
  value: boolean | undefined
  field: string
}

const TabAvanzamento: React.FC<{ immobile: RedImmobile; onUpdate: () => void }> = ({ immobile }) => {
  const livelli = [
    { code: 'P1', label: 'P1 - Rilievo e documentazione', desc: 'Stato di fatto, rilievi, schede FAST/AEDES' },
    { code: 'P2', label: 'P2 - Progettazione preliminare', desc: 'Progetto architettonico, strutturale, acustica' },
    { code: 'P3', label: 'P3 - Progettazione esecutiva', desc: 'CME, QTE, pratiche edilizie, permessi' },
    { code: 'P4', label: 'P4 - Esecuzione lavori', desc: 'Appalto, SAL, collaudo, agibilità' },
  ]

  const currentLivello = immobile.livello_operativo || ''

  const documenti: CheckItem[] = [
    { label: 'Scheda FAST', value: immobile.scheda_fast, field: 'scheda_fast' },
    { label: 'Scheda AEDES', value: immobile.scheda_aedes, field: 'scheda_aedes' },
    { label: 'Planimetria', value: immobile.planimetria, field: 'planimetria' },
  ]

  const progettazione: CheckItem[] = [
    { label: 'Stato di fatto', value: immobile.stato_di_fatto, field: 'stato_di_fatto' },
    { label: 'Progetto architettonico', value: immobile.progetto_architettonico, field: 'progetto_architettonico' },
    { label: 'Progetto strutturale', value: immobile.progetto_strutturale, field: 'progetto_strutturale' },
    { label: 'Progetto acustica', value: immobile.progetto_acustica, field: 'progetto_acustica' },
    { label: 'Relazione geologica', value: immobile.relazione_geologica, field: 'relazione_geologica' },
    { label: 'CME (Computo Metrico Estimativo)', value: immobile.cme, field: 'cme' },
    { label: 'QTE (Quadro Tecnico Economico)', value: immobile.qte, field: 'qte' },
  ]

  const usp = immobile.pratica_usr?.importo_approvato || 0
  const contrib = immobile.importo_contributo || 0
  const prezzo = immobile.prezzo_acquisto || 0
  const acconto = immobile.acconto_pagato || 0
  const saldo = immobile.saldo_residuo || 0

  const percentAcconto = prezzo > 0 ? Math.round((acconto / prezzo) * 100) : 0
  const percentSaldo = prezzo > 0 ? Math.round((saldo / prezzo) * 100) : 0

  return (
    <div className="max-w-4xl space-y-6">
      {/* Livello operativo */}
      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Livello operativo</h2>
        <div className="grid grid-cols-1 gap-3">
          {livelli.map(l => {
            const active = currentLivello === l.code
            const completed = currentLivello && ['P1','P2','P3','P4'].indexOf(currentLivello) >= ['P1','P2','P3','P4'].indexOf(l.code)
            return (
              <div key={l.code} className={`flex items-center gap-4 p-3 rounded-lg border ${active ? 'border-blue-500 bg-blue-50' : completed ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${active ? 'bg-blue-600 text-white' : completed ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                  {completed ? '✓' : l.code}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">{l.label}</div>
                  <div className="text-xs text-gray-500">{l.desc}</div>
                </div>
                {active && <span className="text-xs font-medium text-blue-600">IN CORSO</span>}
                {completed && !active && <span className="text-xs font-medium text-green-600">COMPLETATO</span>}
              </div>
            )
          })}
        </div>
      </div>

      {/* Documentazione */}
      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Documentazione corrente</h2>
        <div className="grid grid-cols-3 gap-3">
          {documenti.map(d => (
            <div key={d.field} className={`flex items-center gap-2 p-2 rounded ${d.value ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'}`}>
              <span className="text-lg">{d.value ? '✓' : '○'}</span>
              <span className="text-sm">{d.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Progettazione */}
      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Stato progettazione</h2>
        <div className="grid grid-cols-2 gap-3">
          {progettazione.map(p => (
            <div key={p.field} className={`flex items-center gap-2 p-2 rounded ${p.value ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-400'}`}>
              <span className="text-lg">{p.value ? '✓' : '○'}</span>
              <span className="text-sm">{p.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Progresso economico */}
      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Progresso economico</h2>
        <div className="space-y-4">
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Acconto pagato</span>
              <span className="font-medium">{acconto.toLocaleString('it-IT')} € ({percentAcconto}%)</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${percentAcconto}%` }} />
            </div>
          </div>
          <div>
            <div className="flex justify-between text-sm mb-1">
              <span>Saldo residuo</span>
              <span className="font-medium">{saldo.toLocaleString('it-IT')} € ({percentSaldo}%)</span>
            </div>
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div className="h-full bg-orange-500 rounded-full" style={{ width: `${percentSaldo}%` }} />
            </div>
          </div>
          {usp > 0 && (
            <div className="pt-2 border-t">
              <div className="flex justify-between text-sm">
                <span>Contributo USR approvato</span>
                <span className="font-medium text-blue-600">{usp.toLocaleString('it-IT')} €</span>
              </div>
              {contrib > 0 && (
                <div className="flex justify-between text-sm mt-1">
                  <span>Contributo concesso</span>
                  <span className="font-medium text-green-600">{contrib.toLocaleString('it-IT')} €</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Dati pratica */}
      <div className="card p-5">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Dati pratica USR</h2>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div><span className="text-gray-500">Codice fascicolo:</span> <span className="font-medium">{immobile.codice_fascicolo || '—'}</span></div>
          <div><span className="text-gray-500">Protocollo:</span> <span className="font-medium">{immobile.protocollo_usr || '—'}</span></div>
          <div><span className="text-gray-500">Delibera:</span> <span className="font-medium">{immobile.delibera || '—'}</span></div>
          <div><span className="text-gray-500">Decreto contributo:</span> <span className="font-medium">{immobile.decreto_contributo || '—'}</span></div>
          <div><span className="text-gray-500">Istruttore:</span> <span className="font-medium">{immobile.istruttore_pratica || '—'}</span></div>
          <div><span className="text-gray-500">Accollo:</span> <span className="font-medium">{immobile.accollo ? 'Sì' : 'No'}</span></div>
          <div><span className="text-gray-500">Tipologia:</span> <span className="font-medium">{immobile.privato_o_condominio || '—'}</span></div>
          <div><span className="text-gray-500">Condominio:</span> <span className="font-medium">{immobile.nome_condominio || '—'}</span></div>
        </div>
      </div>
    </div>
  )
}

export default TabAvanzamento
